const round3 = (value) => Math.round(value * 1000) / 1000;

const quietThreshold = (values) => {
  if (!values?.length) return 0.02;
  const sample = [];
  const step = Math.max(1, Math.floor(values.length / 500));
  for (let i = 0; i < values.length; i += step) sample.push(values[i]);
  sample.sort((a, b) => a - b);
  const low = sample[Math.floor(sample.length * 0.18)] || 0;
  const mid = sample[Math.floor(sample.length * 0.5)] || 0;
  return Math.max(low * 1.4, mid * 0.22, 0.008);
};

/** Propose the next sloka from `fromSec`, covering `lineCount` sung lines. */
export const findNextSpan = (energy, duration, fromSec, previousDuration, lineCount = 2) => {
  const total = Number(duration) || 0;
  const start = Math.min(Math.max(0, Number(fromSec) || 0), Math.max(0, total - 0.4));
  const lines = Math.min(8, Math.max(1, Math.round(Number(lineCount) || 2)));
  const previous = Number(previousDuration) > 2 ? Number(previousDuration) : 0;
  const perLine = previous ? Math.max(3.5, previous / 2) : 8;
  const target = previous ? Math.max(previous, perLine * lines * 0.85) : perLine * lines;
  const fallbackEnd = Math.min(total || start + target, start + target);
  const values = energy?.values;
  const hop = Number(energy?.hop) || 0.05;
  if (!values?.length || total <= start + 0.8) {
    return {
      startSec: round3(start),
      endSec: round3(Math.max(start + 0.8, fallbackEnd)),
      usedGap: false,
      linesFound: 0,
    };
  }

  const maxLen = Math.min(Math.max(0.8, total - start), Math.max(lines * 14, perLine * lines * 2, target * 1.5));
  const startIndex = Math.min(values.length - 1, Math.floor(start / hop));
  const maxIndex = Math.min(values.length - 1, startIndex + Math.floor(maxLen / hop));
  const quiet = quietThreshold(values);
  const gapNeed = Math.max(3, Math.round(0.28 / hop));
  const minSound = Math.max(Math.round(2.2 / hop), Math.floor((perLine * 0.4) / hop));
  const lineEnds = [];
  let heard = 0;
  let gap = 0;
  for (let i = startIndex; i <= maxIndex && lineEnds.length < lines; i += 1) {
    if (values[i] > quiet) {
      heard += 1;
      gap = 0;
      continue;
    }
    gap += 1;
    if (heard >= minSound && gap >= gapNeed) {
      lineEnds.push(i - gap + 1);
      heard = 0;
      gap = 0;
    }
  }

  const foundAll = lineEnds.length >= lines;
  let end = foundAll ? lineEnds[lines - 1] * hop : fallbackEnd;
  end = Math.min(total, Math.max(start + 1.2, end));
  return {
    startSec: round3(start),
    endSec: round3(end),
    usedGap: foundAll,
    linesFound: lineEnds.length,
  };
};

const SLICE_SEC = 45;

const cloudinarySliceUrl = (url, start, end) => {
  const match = String(url || '').match(/^(https?:\/\/res\.cloudinary\.com\/[^/]+\/video\/upload\/)(.+)$/i);
  if (!match) return null;
  return `${match[1]}so_${Math.max(0, Math.floor(start))},eo_${Math.max(1, Math.ceil(end))}/${match[2]}`;
};

const decodeUrl = async (url) => {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) throw new Error('Could not read the audio for the waveform');
  const bytes = await response.arrayBuffer();
  const Context = window.AudioContext || window.webkitAudioContext;
  if (!Context) throw new Error('This browser cannot draw the waveform');
  const context = new Context({ sampleRate: 8000 });
  try {
    return await context.decodeAudioData(bytes);
  } finally {
    try { await context.close(); } catch { /* ignore */ }
  }
};

const measureBuffer = (buffer) => {
  const channel = buffer.getChannelData(0);
  const rate = buffer.sampleRate || 8000;
  const stride = Math.max(1, Math.floor(rate / 4000));
  const energy = [];
  const peaks = [];
  let energySum = 0;
  let energyCount = 0;
  let energyTime = 0;
  let peak = 0;
  let peakTime = 0;
  const step = stride / rate;
  for (let i = 0; i < channel.length; i += stride) {
    const sample = Math.abs(channel[i]);
    energySum += sample;
    energyCount += 1;
    energyTime += step;
    if (sample > peak) peak = sample;
    peakTime += step;
    if (energyTime >= 0.05) {
      energy.push(energyCount ? energySum / energyCount : 0);
      energySum = 0;
      energyCount = 0;
      energyTime = 0;
    }
    if (peakTime >= 1 / 30) {
      peaks.push(peak);
      peak = 0;
      peakTime = 0;
    }
  }
  if (energyCount > 0) energy.push(energySum / energyCount);
  if (peakTime > 0) peaks.push(peak);
  return { peaks, energy, duration: buffer.duration };
};

const sliceCache = new Map();

/** Energy for one saved chapter, so the sung syllable can be followed. */
export async function clipEnergy(url, startSec, endSec) {
  const start = Math.max(0, Number(startSec) || 0);
  const end = Number(endSec) || 0;
  const hop = 0.05;
  if (!url || !(end > start + 0.2)) return { values: [], hop, duration: 0 };

  const sliceStart = Math.floor(start);
  const sliceEnd = Math.max(sliceStart + 1, Math.ceil(end));
  const sliced = cloudinarySliceUrl(url, sliceStart, sliceEnd);
  const key = sliced || url;
  let measured = sliceCache.get(key);
  if (!measured) {
    measured = measureBuffer(await decodeUrl(key));
    sliceCache.set(key, measured);
  }

  const requested = sliceEnd - sliceStart;
  const sliceApplied = Boolean(sliced) && measured.duration <= requested + 5;
  const origin = sliceApplied ? start - sliceStart : start;
  const from = Math.max(0, Math.round(origin / hop));
  const frames = Math.max(1, Math.round((end - start) / hop));
  return {
    values: measured.energy.slice(from, from + frames),
    hop,
    duration: frames * hop,
  };
}

const placeSlice = (peaks, energy, local, offsetSec) => {
  const peakAt = Math.round(offsetSec * 30);
  const energyAt = Math.round(offsetSec / 0.05);
  while (peaks.length < peakAt) peaks.push(0);
  while (energy.length < energyAt) energy.push(0);
  peaks.push(...local.peaks);
  energy.push(...local.energy);
};

export async function analyzeAudioUrl(url, { durationHint = 0, onUpdate } = {}) {
  const known = Number(durationHint) || 0;
  const sliced = known > SLICE_SEC + 5 && cloudinarySliceUrl(url, 0, SLICE_SEC);
  if (!sliced) {
    const measured = measureBuffer(await decodeUrl(url));
    const result = {
      duration: measured.duration || known,
      peaks: measured.peaks,
      energy: { hop: 0.05, values: measured.energy },
      partial: false,
    };
    onUpdate?.(result);
    return result;
  }

  const peaks = [];
  const energy = [];
  for (let start = 0; start < known - 0.25; start += SLICE_SEC) {
    const end = Math.min(known, start + SLICE_SEC);
    const pieceUrl = cloudinarySliceUrl(url, start, end);
    const measured = measureBuffer(await decodeUrl(pieceUrl));
    if (measured.duration > SLICE_SEC + 20) {
      const whole = measureBuffer(await decodeUrl(url));
      const result = {
        duration: whole.duration || known,
        peaks: whole.peaks,
        energy: { hop: 0.05, values: whole.energy },
        partial: false,
      };
      onUpdate?.(result);
      return result;
    }
    placeSlice(peaks, energy, measured, start);
    const partial = end < known - 0.25;
    onUpdate?.({
      duration: known,
      peaks: peaks.slice(),
      energy: { hop: 0.05, values: energy.slice() },
      partial,
    });
  }
  return {
    duration: known,
    peaks,
    energy: { hop: 0.05, values: energy },
    partial: false,
  };
}
